import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from './firebase'; 
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, getDocs, orderBy, query, doc, getDoc, where } from "firebase/firestore";
import './Home.css';

// --- HELPER: 5:00 PM IST CLOSING RULE ---

const isNewNotice = (dateString) => {
  if (!dateString) return false;
  const noticeDate = new Date(dateString);
  const diffDays = (new Date() - noticeDate) / (1000 * 60 * 60 * 24);
  return diffDays <= 5; 
};

const isTenderClosed = (dateString, timeString) => {
  if (!dateString) return false;
  const time = timeString || "18:30"; // Fallback for older tenders
  const closeTime = new Date(`${dateString}T${time}:00+05:30`);
  const now = new Date();
  return now > closeTime;
};

function Home() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null); 
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  
  // NEW: Mobile Menu State
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Data States
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [allTenders, setAllTenders] = useState([]);
  const [displayedTenders, setDisplayedTenders] = useState([]);
  const [committee, setCommittee] = useState([]); 
  const [showAllCommittee, setShowAllCommittee] = useState(false); 
  const [allNotices, setAllNotices] = useState([]); 
  const [displayedNotices, setDisplayedNotices] = useState([]); 
  
  // Pagination State
  const [noticePage, setNoticePage] = useState(1);
  const noticesPerPage = 4; 
  const [totalNoticePages, setTotalNoticePages] = useState(1);

  const [tenderPage, setTenderPage] = useState(1);
  const tendersPerPage = 5;
  const [totalTenderPages, setTotalTenderPages] = useState(1);

  // Result Popup State
  const [resultPopup, setResultPopup] = useState(null); 
  const [resultBids, setResultBids] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);

  const isClosingSoon = (dateString) => {
  if (!dateString) return false;
  const closeDate = new Date(`${dateString}T21:00:00+05:30`);
  const daysLeft = (closeDate - new Date()) / (1000 * 60 * 60 * 24);
  return daysLeft > 0 && daysLeft <= 3;
};

  // --- GALLERY LOGIC ---
  const allGalleryImages = [
    "/gallery/image1.jpg", 
    "/gallery/image2.jpg",
    "/gallery/image3.jpg",
    "/gallery/image4.jpg",
    "/gallery/image5.jpg",
    "/gallery/image6.jpg"
  ];
  const [displayImages, setDisplayImages] = useState([]);

  // Auto-shuffle Gallery
  useEffect(() => {
    const shuffleImages = () => {
      const shuffled = [...allGalleryImages].sort(() => 0.5 - Math.random());
      setDisplayImages(shuffled.slice(0, 4));
    };
    
    shuffleImages(); 
    const interval = setInterval(shuffleImages, 5000); 
    
    return () => clearInterval(interval);
  }, []);

  // --- 1. AUTH & ROLE LISTENER ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
            const userRef = doc(db, "users", user.uid);
            const snap = await getDoc(userRef);
            if(snap.exists()) {
                setUserRole(snap.data().role);
            }
        } catch (e) { console.error("Error fetching role", e); }
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }
      setIsAuthLoading(false); // <--- ADD THIS: Tells UI fetching is done
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    window.location.reload();
  };

  const handleDashboardClick = () => {
      if (userRole === 'admin') navigate('/admin');
      else navigate('/bidder-dashboard');
  };

  // --- 2. FETCH DATA ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const qTenders = query(collection(db, "tenders"), orderBy("createdAt", "desc"));
        const tendersSnap = await getDocs(qTenders);
        const fetchedTenders = tendersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        setAllTenders(fetchedTenders);
        setTotalTenderPages(Math.ceil(fetchedTenders.length / tendersPerPage));
        setDisplayedTenders(fetchedTenders.slice(0, tendersPerPage));

        const qCommittee = query(collection(db, "committee"), orderBy("order", "asc"));
        const committeeSnap = await getDocs(qCommittee);
        setCommittee(committeeSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const qAnnouncements = query(collection(db, "announcements"), orderBy("date", "desc"));
        const noticesSnap = await getDocs(qAnnouncements);
        const fetchedNotices = noticesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        setAllNotices(fetchedNotices);
        setTotalNoticePages(Math.ceil(fetchedNotices.length / noticesPerPage));
        setDisplayedNotices(fetchedNotices.slice(0, noticesPerPage));
      } catch (error) { console.error(error); }finally {
        setIsPageLoading(false); 
      }
    };
    fetchData();
  }, []);

  // --- 3. HANDLERS ---
  const handleNoticePageChange = (pageNumber) => {
    setNoticePage(pageNumber);
    const start = (pageNumber - 1) * noticesPerPage;
    setDisplayedNotices(allNotices.slice(start, start + noticesPerPage));
  };

  const handleTenderPageChange = (pageNumber) => {
    setTenderPage(pageNumber);
    const start = (pageNumber - 1) * tendersPerPage;
    setDisplayedTenders(allTenders.slice(start, start + tendersPerPage));
  };

  // --- 4. RESULT POPUP HANDLER ---
  const openResult = async (tender) => {
    setResultPopup(tender);
    setLoadingResults(true);
    setResultBids([]);
    try {
        const q = query(collection(db, "bids"), where("tenderId", "==", tender.id));
        const snap = await getDocs(q);
        const bids = snap.docs.map(d => d.data());
        bids.sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount));
        setResultBids(bids);
    } catch (e) { console.error("Error fetching results:", e); }
    setLoadingResults(false);
  };

  return (
    <div className="home-wrapper">
      
      {/* HEADER */}
      <header className="modern-header">
        <div className="container header-flex">
          <div className="brand-box flex-center">
            <img src="/logo.svg" alt="Logo" className="nav-logo" onError={(e) => e.target.style.display='none'} />
          </div>
          
          {/* DESKTOP NAVIGATION */}
          <div className="desktop-nav">
            <nav className="nav-links">
              
              <a href="#announcements">Announcements</a>
              <a href="#tenders">Tenders</a>
              <a href="#gallery">Gallery</a>
              <a href="/contact" onClick={(e) => { e.preventDefault(); navigate('/contact'); }}>Contact</a>
            </nav>
            <div className="nav-actions">
              {isAuthLoading ? (
                 <button className="btn-primary" style={{opacity: 0.7}}>Loading...</button>
              ) : currentUser ? (
                <div style={{display:'flex', gap:'10px', alignItems: 'center'}}>
                  <button onClick={handleDashboardClick} className="btn-primary">
                      {userRole === 'admin' ? 'Admin Panel' : 'Vendor Dashboard'}
                  </button>
                  <button className="btn-logout" onClick={handleLogout}>Logout</button>
                </div>
              ) : (
                  <button className="btn-primary" onClick={() => navigate('/login')}>Login</button>
              )}
            </div>
          </div>

          {/* MOBILE HAMBURGER BUTTON */}
          <button className="mobile-menu-btn" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? '✖' : '☰'}
          </button>
        </div>

        {/* MOBILE DROPDOWN MENU */}
        {isMobileMenuOpen && (
          <div className="mobile-dropdown">
            <nav className="mobile-nav-links">
              
              <a href="#announcements" onClick={() => setIsMobileMenuOpen(false)}>Announcements</a>
              <a href="#tenders" onClick={() => setIsMobileMenuOpen(false)}>Tenders</a>
              <a href="#gallery" onClick={() => setIsMobileMenuOpen(false)}>Gallery</a>
              <a href="/contact" onClick={(e) => { e.preventDefault(); navigate('/contact'); setIsMobileMenuOpen(false); }}>Contact</a>
            </nav>
            <div className="mobile-auth-actions">
              {isAuthLoading ? (
                 <button className="btn-primary" style={{opacity: 0.7}}>Loading...</button>
              ) : currentUser ? (
                <>
                  <button onClick={() => { handleDashboardClick(); setIsMobileMenuOpen(false); }} className="btn-primary">
                      {userRole === 'admin' ? 'Admin Panel' : 'Vendor Dashboard'}
                  </button>
                  <button className="btn-logout" onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}>Logout</button>
                </>
              ) : (
                  <button className="btn-primary" onClick={() => { navigate('/login'); setIsMobileMenuOpen(false); }}>Login</button>
              )}
            </div>
          </div>
        )}
      </header>

      <main>
        {/* HERO SECTION */}
        <section className="hero-section">
          <div className="hero-bg">
  <video
    autoPlay
    muted
    playsInline
    poster="/gallery/image6.jpg"
    className="hero-video"
  >
    <source src="/video.webm" type="video/webm" />
  </video>
</div>
          <div className="hero-overlay"></div>
          <div className="container hero-content relative z-10">
            <span className="hero-badge">BATHINDA'S FINEST LIVING</span>
            <h1 className="hero-title">Welcome to <br/> <span className="text-highlight">Housefed Colony</span></h1>
            <p className="hero-subtitle">Experience a modern, vibrant, and transparent community designed for the future of urban living in Bathinda.</p>
          </div>
        </section>

        {/* ANNOUNCEMENTS & COMMITTEE (ASYMMETRIC LAYOUT) */}
        <section className="section-padding bg-surface-low" id="announcements">
          <div className="container grid-asymmetric">
            
            {/* Announcements */}
            <div className="card-editorial border-left-highlight">
              <div className="card-header flex-between mb-8">
                <div className="flex-center gap-4">
                  <div className="icon-box-primary">📢</div>
                  <h2 className="section-title">Latest Announcements</h2>
                </div>
              </div>
              <div className="announcement-list">
                {isPageLoading ? (
  <p className="loading-pulse">Fetching announcements...</p>
) : displayedNotices.length === 0 ? (
  <p>No announcements yet.</p>
) : (
                  displayedNotices.map((item) => (
                    <div key={item.id} className="news-item group">
                      <div className="news-meta">
  <span className="news-category">Update</span>
  <div>
    {isNewNotice(item.date) && <span className="badge-new">NEW</span>}
    <span className="news-date-text">{new Date(item.date).toLocaleDateString()}</span>
  </div>
</div>
                      <h3 className="news-title">{item.title}</h3>
                      <p className="news-desc">{item.description}</p>
                      {item.fileUrl && (<a href={item.fileUrl} target="_blank" rel="noreferrer" className="link-download">Download PDF</a>)}
                    </div>
                  ))
                )}
              </div>
              {/* Pagination Preserved */}
              {totalNoticePages > 1 && (
                  <div className="pagination-controls mt-6">
                      {Array.from({ length: totalNoticePages }, (_, i) => i + 1).map((pageNum) => (
                          <button key={pageNum} onClick={() => handleNoticePageChange(pageNum)} className={`page-btn ${noticePage === pageNum ? 'active' : ''}`}>{pageNum}</button>
                      ))}
                  </div>
              )}
            </div>

            {/* Committee */}
            <div className="card-primary flex-col">
              <div className="mb-8">
                <div className="icon-box-glass mb-4">👥</div>
                <h2 className="section-title text-white">Management Committee</h2>
                <p className="text-light-dim mt-2">Dedicated leadership ensuring the smooth operation and continuous development of Housefed Colony.</p>
              </div>
              
              <div className="committee-list flex-grow">
                {isPageLoading ? (
  <p className="loading-pulse text-white">Loading committee members...</p>
) : committee.length === 0 ? (
  <p className="text-white">No members added yet.</p>
) : (
                  committee.slice(0, showAllCommittee ? committee.length : 2).map((member) => (
                    <div key={member.id} className="committee-member-card">
                      <div className="member-avatar">
                        {member.photoUrl ? <img src={member.photoUrl} alt={member.name} /> : <span>{member.name.charAt(0)}</span>}
                      </div>
                      <div>
                        <p className="member-name">{member.name}</p>
                        <p className="member-role">{member.role}</p>
                        <p className="member-phone">📞 {member.phone}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {committee.length > 2 && (
                <button className="btn-text-white mt-6" onClick={() => setShowAllCommittee(!showAllCommittee)}>
                  {showAllCommittee ? "Show Less ▴" : "Meet all members ▾"}
                </button>
              )}
            </div>

          </div>
        </section>

        {/* --- RESIDENT & MEMBER PORTAL CTA --- */}
        <section className="section-padding bg-surface reveal-on-scroll" id="member-portal">
          <div className="container">
            <div className="member-cta-card">
              
              <div className="cta-content">
                <div className="flex-center-left gap-4 mb-4">
                  <div className="icon-box-primary payment-icon">💳</div>
                  <h2 className="section-title">Resident & Member Portal</h2>
                </div>
                
                <p className="text-subtitle mb-8" style={{ maxWidth: '600px' }}>
                Log in to your dashboard to easily pay monthly maintenance, annual charges, fines, and other society fees.
                </p>
                
                <button 
  className="btn-red flex-center gap-2" 
  style={{ width: 'fit-content' }} 
  onClick={() => navigate('/login')}
>
                  Go to Member Login <span className="arrow-slide">→</span>
                </button>
              </div>

              {/* Dynamic Background Orbs */}
              <div className="cta-graphics hidden-md">
                <div className="glow-orb orb-1"></div>
                <div className="glow-orb orb-2"></div>
              </div>

            </div>
          </div>
        </section>

        {/* ACTIVE TENDERS */}
        <section className="section-padding bg-surface" id="tenders">
          <div className="container">
            <div className="flex-between flex-wrap mb-8">
              <div>
                <div className="flex-center-left gap-4 mb-2">
  <div className="icon-box-primary">🔨</div>
  <h2 className="section-title-large" style={{ margin: 0 }}>Active Tenders</h2>
</div>
                
                <p className="text-subtitle">Transparent procurement for community development.</p>
              </div>
            </div>

            <div className="card-editorial overflow-hidden">
              <div className="table-responsive">
                {isPageLoading ? (
  <div className="p-6 loading-pulse">Loading active tenders, please wait...</div>
) : displayedTenders.length === 0 ? (
  <div className="p-6">No active tenders found.</div>
) : (
                  <table className="modern-table w-full">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Opens</th>
                        <th>Closes</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedTenders.map((t) => {
                        const isClosed = isTenderClosed(t.closeDate, t.closeTime);
                        return (
                          <tr key={t.id} className="table-row-hover">
                            {/* NEW: Added data-label properties so mobile CSS can render the titles */}
                            <td data-label="Title" className="font-bold text-primary max-w-xs">{t.title}</td>
                            <td data-label="Opens" className="text-sm">{t.openDate}</td>
                            <td data-label="Closes" className="text-sm">{t.closeDate}</td>
                            <td data-label="Status">
  {isClosed ? (
     <span className="badge-closed">CLOSED</span>
  ) : isClosingSoon(t.closeDate) ? (
     <span className="badge-warning">⏳ CLOSING SOON</span>
  ) : (
     <span className="badge-live"><span className="pulse-dot"></span> LIVE</span>
  )}
</td>
                            <td data-label="Actions">
                                <div className="action-buttons-flex">
                                    {t.fileUrl ? (
                                        <a href={t.fileUrl} target="_blank" rel="noreferrer" className="btn-action-outline">Document</a>
                                    ) : (
                                        <button className="btn-action-outline disabled">No Doc</button>
                                    )}

                                    {isClosed ? (
                                        <button onClick={() => openResult(t)} className="btn-action-solid">Result</button>
                                    ) : (
                                        <button onClick={() => {
                                            if(!currentUser) navigate('/login');
                                            else if(userRole === 'admin') navigate('/admin');
                                            else navigate('/bidder-dashboard');
                                        }} className="btn-action-red">Apply</button>
                                    )}
                                </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            {/* Tender Pagination Preserved */}
            {totalTenderPages > 1 && (
                <div className="pagination-controls mt-6">
                    {Array.from({ length: totalTenderPages }, (_, i) => i + 1).map((pageNum) => (
                        <button key={pageNum} onClick={() => handleTenderPageChange(pageNum)} className={`page-btn ${tenderPage === pageNum ? 'active' : ''}`}>{pageNum}</button>
                    ))}
                </div>
            )}
          </div>
        </section>

        {/* SOCIETY GALLERY (BENTO GRID) */}
        <section className="section-padding bg-surface-low" id="gallery">
          <div className="container">
            <div className="flex-center-left gap-4 mb-10">
  <div className="icon-box-glass">🖼️</div>
  <h2 className="section-title-large" style={{ margin: 0 }}>Gallery</h2>
  <div className="divider-line"></div>
</div>

            {displayImages.length >= 4 && (
                <div className="bento-gallery">
                  {/* Left Large Image */}
                  <div className="bento-item bento-large group">
                    <img src={displayImages[0]} alt="Gallery 1" />
                  </div>
                  {/* Right 3 Images */}
                  <div className="bento-item group"><img src={displayImages[1]} alt="Gallery 2" /></div>
                  <div className="bento-item group"><img src={displayImages[2]} alt="Gallery 3" /></div>
                  <div className="bento-item group"><img src={displayImages[3]} alt="Gallery 4" /></div>
                </div>
            )}
          </div>
        </section>

        {/* MAP SECTION */}
        <section className="container map-section mb-16">
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13796.965654558844!2d74.91655758714634!3d30.173097111244342!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39172d3f8b26daa9%3A0x54bcb4ed8c256a87!2sHousefed%20Colony%2C%20Bathinda%2C%20Punjab%20151001%2C%20India!5e0!3m2!1sen!2sus!4v1767715419946!5m2!1sen!2sus" 
            width="100%" 
            height="450" 
            style={{ border: 0, borderRadius: '24px' }} 
            allowFullScreen="" 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade" 
            title="Society Map">
          </iframe>
        </section>

      </main>

      {/* NEW FOOTER */}
      <footer className="modern-site-footer">
        <div className="container footer-grid">
          <div>
            <div className="flex-center-left mb-4">
              <img src="/logo.svg" alt="Logo" className="footer-logo" onError={(e) => e.target.style.display='none'} />
              
            </div>
            <p className="footer-desc">Officially know as "The Bathinda Housing Complex Cooperative House Building Society Limited".</p>
          </div>
          <div className="footer-contact">
            <h4 className="footer-heading">Contact Details</h4>
            <p>📱 Mobile: +91 9872348700</p>
            <p>✉️ Email: bhcchbs@gmail.com</p>
            <p>📍 Address: Housefed Colony, Bathinda, Punjab, India 151001</p>
          </div>
          <div>
            <h4 className="footer-heading">Quick Links</h4>
            <ul className="footer-links">
              <li><button className="link-style-btn" onClick={() => navigate('/privacy')}>Privacy Policy</button></li>
              <li><button className="link-style-btn" onClick={() => navigate('/terms')}>Terms and Conditions</button></li>
              <li><button className="link-style-btn" onClick={() => navigate('/refund-policy')}>Refund Policy</button></li>
              <li><button className="link-style-btn" onClick={() => navigate('/contact')}>Contact Us</button></li>
            </ul>
          </div>
        </div>
        <div className="container footer-bottom">
          <p>© 2024 Housefed Colony Bathinda. All rights reserved.</p>
          <a href="/developer" onClick={(e) => { e.preventDefault(); navigate('/developer'); }} className="footer-boost" style={{cursor: 'pointer'}}>
  Boost your Business, make your own website.
</a>
        </div>
      </footer>

      {/* RESULT POPUP (PRESERVED LOGIC) */}
      {resultPopup && (
        <div className="modal-overlay" onClick={() => setResultPopup(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3>Tender Result</h3><button onClick={() => setResultPopup(null)}>✖</button></div>
            <div className="modal-body">
              <h4>{resultPopup.title}</h4>
              <p className="ref-text">Ref: {resultPopup.id.slice(0,6).toUpperCase()}</p>
              {loadingResults ? ( <div className="p-6">Loading Results...</div> ) : (
                resultBids.length === 0 ? ( <div className="result-placeholder"><p>No bids were submitted for this tender.</p></div> ) : (
                    <div className="table-responsive">
                        <table className="modern-table result-table w-full">
                            <thead><tr><th>Rank</th><th>Bidder Name</th></tr></thead>
                            <tbody>
                                {resultBids.map((bid, index) => {
                                    let rowClass = index === 0 ? "row-gold" : index === 1 ? "row-silver" : "";
                                    let rankDisplay = index === 0 ? "🏆 L1" : index === 1 ? "🥈 L2" : index + 1;
                                    return (
    <tr key={index} className={rowClass}>
        <td><strong>{rankDisplay}</strong></td>
        <td>{bid.bidderName}</td>
    </tr>
);
                                })}
                            </tbody>
                        </table>
                    </div>
                )
              )}
            </div>
            <div className="modal-footer"><button onClick={() => setResultPopup(null)} className="btn-action-solid">Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;